import "../types/index.js";

import { Axis } from "../constants/axis.js";
import { Direction } from "../constants/direction.js";
import { PanEvent } from "../events/pan_event.js";
import { createFragment } from "../utilities/elements.js";
import { supportsTouch } from "../utilities/media.js";
import { onResize } from "../utilities/resize.js";
import { DickTokStandaloneComponent } from "./StandaloneComponent.js";
import { defineElement } from "./registry.js";

export class DickTokPanSurface extends DickTokStandaloneComponent {
  static attributes = [
    { name: "axes", type: Array, optional: true },
    { name: "pan-distance", type: Number, optional: true },
    { name: "swipe-velocity", type: Number, optional: true },
  ];

  static template = createFragment(`
      <div data-target="surface">
        <slot></slot>
      </div>
    `);

  static targets = ["surface"];

  /** Configuration */

  /** @type {Set<Axis>} */
  #enabledAxes = new Set();
  /** @type {number} */
  #minPanDistancePx = 10;
  /** @type {number} */
  #minSwipeVelocityPxMs = 0.3;

  /** Dimensions */

  /** @type {Coordinates} */
  #origin;
  /** @type {number} */
  #height;
  /** @type {number} */
  #width;

  /** Pan state */

  /** @type {boolean} */
  #isPanning = false;
  /** @type {?PanInput} */
  #panStart = null;
  /** @type {?PanInput} */
  #panEnd = null;
  /** @type {?Axis} */
  #panAxis = null;

  connectedCallback() {
    super.connectedCallback();

    // Read configuration from attributes
    this.hasAxes
      ? this.#enableAxes(this.axes)
      : this.#enableAxes([Axis.HORIZONTAL, Axis.VERTICAL]);
    this.hasPanDistance && (this.#minPanDistancePx = this.panDistance);
    this.hasSwipeVelocity && (this.#minSwipeVelocityPxMs = this.swipeVelocity);

    this.#measureElement();
    this.#updateCssProperties();
    supportsTouch() ? this.#addTouchListeners() : this.#addMouseListeners();
  }

  #measureElement() {
    const { top, left, height, width } = this.getBoundingClientRect();
    this.#origin = { x: left, y: top };
    this.#height = height;
    this.#width = width;
    // We need to observe an element within the shadowRoot to get the correct
    // size, observing the host returns 0 for height and width. This won't
    // handle if the origin changes, but we don't expect that for now.
    onResize(this.surfaceTarget, this.disconnectSignal, ({ contentRect }) => {
      this.#height = contentRect.height;
      this.#width = contentRect.width;
    });
  }

  #updateCssProperties() {
    this.surfaceTarget.style.setProperty("user-select", "none");
    if (this.#enabledAxes.size === 2) {
      this.surfaceTarget.style.setProperty("overflow-behavior", "none");
      this.surfaceTarget.style.setProperty("touch-action", "none");
    } else if (this.#enabledAxes.has(Axis.HORIZONTAL)) {
      this.surfaceTarget.style.setProperty("overflow-behavior", "none revert");
      // This is inverted because its what the BROWSER handles
      this.surfaceTarget.style.setProperty("touch-action", "pan-y");
    } else if (this.#enabledAxes.has(Axis.VERTICAL)) {
      this.surfaceTarget.style.setProperty("overflow-behavior", "revert none");
      // This is inverted because its what the BROWSER handles
      this.surfaceTarget.style.setProperty("touch-action", "pan-x");
    } else {
      this.surfaceTarget.style.setProperty("overflow-behavior", "revert");
      this.surfaceTarget.style.setProperty("touch-action", "revert");
      this.surfaceTarget.style.setProperty("user-select", "revert");
    }
  }

  #addTouchListeners() {
    const options = {
      capture: true,
      passive: true,
      signal: this.disconnectSignal,
    };
    this.surfaceTarget.addEventListener(
      "touchstart",
      /** @param {TouchEvent} event */ (event) => {
        if (event.touches.length === 1) {
          const touch = event.touches[0];
          this.#handlePanStart({ x: touch.clientX, y: touch.clientY });
        }
      },
      options,
    );
    this.surfaceTarget.addEventListener(
      "touchmove",
      /** @param {TouchEvent} event */ (event) => {
        if (event.touches.length === 1) {
          const touch = event.touches[0];
          this.#handlePanMove({ x: touch.clientX, y: touch.clientY });
          if (this.#isPanning) {
            // If panning, prevent event from propagating to children
            event.stopPropagation();
          }
        }
      },
      options,
    );
    this.surfaceTarget.addEventListener(
      "touchend",
      /** @param {TouchEvent} event */ (event) => {
        // Do this first as #handlePanEnd() will set isPanning = false
        if (this.#isPanning) {
          // If panning, prevent event from propagating to children
          event.stopPropagation();
        }
        this.#handlePanEnd();
      },
      options,
    );
    this.surfaceTarget.addEventListener(
      "touchcancel",
      /** @param {TouchEvent} event */ (event) => {
        // Do this first as #handlePanCancel() will set isPanning = false
        if (this.#isPanning) {
          // If panning, prevent event from propagating to children
          event.stopPropagation();
        }
        this.#handlePanCancel();
      },
      options,
    );
  }

  #addMouseListeners() {
    const options = {
      capture: true,
      passive: true,
      signal: this.disconnectSignal,
    };
    this.surfaceTarget.addEventListener(
      "mousedown",
      /** @param {MouseEvent} event */ (event) => {
        this.#handlePanStart({ x: event.clientX, y: event.clientY });
      },
      options,
    );
    this.surfaceTarget.addEventListener(
      "mousemove",
      /** @param {MouseEvent} event */ (event) => {
        // Mouse emits this event all the time, so don't update unless
        // mousedown has fired and mouseup has not fired.
        if (!this.#panStart) {
          return;
        }
        this.#handlePanMove({ x: event.clientX, y: event.clientY });
        if (this.#isPanning) {
          // If panning, prevent events from propagating to children
          event.stopPropagation();
        }
      },
      options,
    );
    this.surfaceTarget.addEventListener(
      "mouseup",
      /** @param {MouseEvent} event */ (event) => {
        if (!this.#panStart) {
          return;
        }
        // Do this first as #handlePanEnd() will set isPanning = false
        if (this.#isPanning) {
          // If panning, prevent events from propagating to children
          event.stopPropagation();
        }
        this.#handlePanEnd();
      },
      options,
    );
    this.surfaceTarget.addEventListener(
      "mouseout",
      /** @param {MouseEvent} event */ (event) => {
        if (!this.#panStart) {
          return;
        }
        // Do this first as #handlePanCancel() will set isPanning = false
        if (this.#isPanning) {
          // If panning, prevent events from propagating to children
          event.stopPropagation();
        }
        this.#handlePanCancel();
      },
      options,
    );
  }

  /** @param {Axis[]} axes */
  #enableAxes(axes) {
    axes.forEach((axis) => {
      this.#enabledAxes.add(axis);
    });
    this.#updateCssProperties();
  }

  /** @param {Axis[]} axes */
  #disableAxes(axes) {
    axes.forEach((axis) => {
      this.#enabledAxes.delete(axis);
    });
    this.#updateCssProperties();
  }

  /** @param {Coordinates} coordinates Client coordinates */
  #handlePanStart(coordinates) {
    if (this.#enabledAxes.size === 0) {
      return;
    }

    const { native, normalized } = this.#calculatePanCoordinates(coordinates);
    this.#panStart = { native, normalized, time: Date.now() };
  }

  /** @param {Coordinates} coordinates Client coordinates */
  #handlePanMove(coordinates) {
    if (this.#enabledAxes.size === 0) {
      return;
    }

    const { native, normalized } = this.#calculatePanCoordinates(coordinates);
    this.#panEnd = { native, normalized, time: Date.now() };
    const panMeasurement = this.#measurePan(
      this.#panStart,
      this.#panEnd,
      this.#panAxis,
    );

    // When the native start event fires, the user has only touched the screen.
    // This could be a tap, so the gesture should only be considered a pan once
    // the user pans a minimum distance.
    if (!this.#isPanning) {
      const { distance } = panMeasurement;

      // Check if the user has panned the minimum distance
      if (
        !(
          distance.native.x > this.#minPanDistancePx ||
          distance.native.y > this.#minPanDistancePx
        )
      ) {
        return;
      }

      // Check if we are tracking this axis
      if (!this.#enabledAxes.has(panMeasurement.axis)) {
        return;
      }

      // Update component state
      this.#isPanning = true;
      // Don't let the axis to change once the gesture is started
      this.#panAxis = panMeasurement.axis;

      // Dispatch event on this element for the pan start
      /** @type {CustomEventInit<PanEventDetail>} */
      const eventOptions = {
        bubbles: true,
        composed: true,
        detail: {
          ...panMeasurement,
          start: this.#panStart,
          end: this.#panEnd,
        },
      };
      this.dispatchEvent(new CustomEvent(PanEvent.PAN_START, eventOptions));
    }

    if (this.#isPanning) {
      if (!this.#enabledAxes.has(panMeasurement.axis)) {
        return;
      }

      /** @type {CustomEventInit<PanEventDetail>} */
      const eventOptions = {
        bubbles: true,
        composed: true,
        detail: {
          ...panMeasurement,
          start: this.#panStart,
          end: this.#panEnd,
        },
      };
      this.dispatchEvent(new CustomEvent(PanEvent.PAN_MOVE, eventOptions));
    }
  }

  #handlePanEnd() {
    if (this.#enabledAxes.size === 0) {
      return;
    }

    // The user made a touch gesture but it never exceeded the minimum distance
    // to be considered a pan
    if (!this.#isPanning) {
      return;
    }

    // panMeasurement has not actually changed since the last move, since the
    // end event has no client coordindates
    const panMeasurement = this.#measurePan(this.#panStart, this.#panEnd);

    if (!this.#enabledAxes.has(panMeasurement.axis)) {
      return;
    }

    /** @type {CustomEventInit<PanEventDetail>} */
    const eventOptions = {
      bubbles: true,
      composed: true,
      detail: {
        ...panMeasurement,
        start: this.#panStart,
        end: this.#panEnd,
        gesture: "pan",
      },
    };

    if (
      panMeasurement.axis === Axis.HORIZONTAL &&
      panMeasurement.velocity.x > this.#minSwipeVelocityPxMs
    ) {
      eventOptions.detail.gesture = "swipe";
      this.dispatchEvent(new CustomEvent(PanEvent.SWIPE, eventOptions));
      this.dispatchEvent(
        panMeasurement.direction === Direction.RIGHT
          ? new CustomEvent(PanEvent.SWIPE_RIGHT, eventOptions)
          : new CustomEvent(PanEvent.SWIPE_LEFT, eventOptions),
      );
    } else if (
      panMeasurement.axis === Axis.VERTICAL &&
      panMeasurement.velocity.y > this.#minSwipeVelocityPxMs
    ) {
      eventOptions.detail.gesture = "swipe";
      this.dispatchEvent(new CustomEvent(PanEvent.SWIPE, eventOptions));
      this.dispatchEvent(
        panMeasurement.direction === Direction.DOWN
          ? new CustomEvent(PanEvent.SWIPE_DOWN, eventOptions)
          : new CustomEvent(PanEvent.SWIPE_UP, eventOptions),
      );
    }

    // Dispatch the pan end event after the swipe gesture event, so that
    // components could decide not to handle the pan end if they handled the
    // swipe instead. However, note that the pan end event detail does contain
    // the gesture; combined with the axis and direction, the same information
    // can be inferred.
    this.dispatchEvent(new CustomEvent(PanEvent.PAN_END, { ...eventOptions }));

    this.#resetPanState();
  }

  #handlePanCancel() {
    this.dispatchEvent(
      new CustomEvent(PanEvent.PAN_CANCEL, { bubbles: true, composed: true }),
    );
    this.#resetPanState();
  }

  /**
   * Calculate pan coordinates relative to the surface origin and normalize
   * coordinates (0-1) relative to the surface dimensions.
   * @param {Coordinates} coordinates Client coordinates (relative to window)
   * @returns {{native: Coordinates, normalized: Coordinates}}
   */
  #calculatePanCoordinates(coordinates) {
    // Subtract the element origin in case the element is not at 0,0
    const nativeX = coordinates.x - this.#origin.x;
    const nativeY = coordinates.y - this.#origin.y;

    // Normalize the value (0-1)
    const normalizedX = nativeX === 0 ? 0 : nativeX / this.#width;
    const normalizedY = nativeY === 0 ? 0 : nativeY / this.#height;

    return {
      native: { x: nativeX, y: nativeY },
      normalized: { x: normalizedX, y: normalizedY },
    };
  }

  /**
   * @param {PanInput} panStart
   * @param {PanInput} panEnd
   * @param {Axis=} axis
   * @returns {PanMeasurement}
   */
  #measurePan(panStart, panEnd, axis = undefined) {
    const delta = {
      native: {
        x: panEnd.native.x - panStart.native.x,
        y: panEnd.native.y - panStart.native.y,
      },
      normalized: {
        x: panEnd.normalized.x - panStart.normalized.x,
        y: panEnd.normalized.y - panStart.normalized.y,
      },
    };

    const distance = {
      native: {
        x: Math.abs(delta.native.x),
        y: Math.abs(delta.native.y),
      },
      normalized: {
        x: Math.abs(delta.normalized.x),
        y: Math.abs(delta.normalized.y),
      },
    };

    if (!axis) {
      axis =
        distance.native.x > distance.native.y ? Axis.HORIZONTAL : Axis.VERTICAL;
    }

    const direction =
      axis === Axis.HORIZONTAL
        ? delta.native.x > 0
          ? Direction.RIGHT
          : Direction.LEFT
        : delta.native.y > 0
          ? Direction.DOWN
          : Direction.UP;

    const duration = panEnd.time - panStart.time;

    const velocity = {
      x: distance.native.x / duration,
      y: distance.native.y / duration,
    };

    return { axis, direction, delta, distance, duration, velocity };
  }

  #resetPanState() {
    this.#isPanning = false;
    this.#panStart = null;
    this.#panEnd = null;
    this.#panAxis = null;
  }
}

defineElement("dt-pansurface", DickTokPanSurface);

export const eventstart = supportsTouch() ? "touchstart" : "mousedown";
export const eventmove = supportsTouch() ? "touchmove" : "mousemove";
export const eventend = supportsTouch() ? "touchend" : "mouseup";
export const eventcancel = supportsTouch() ? "touchcancel" : "mouseout";
